import { useMemo } from 'react';
import clsx from 'clsx';
import Link from 'next/link';

import { useRouter } from 'next/router';

import { Icon } from 'common/icon';
import { ICasino, IProvider, ISlot, IStreamer } from 'constants/types';

import styles from './styles.module.css';
import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { BlurHashImage } from 'components/blurhash-image';
import { Routes } from 'constants/routes';
import { getItemIdentifier } from 'utils/slug';
import { useRTPWeights } from 'hooks/useRTPWeights';
import { getCasinoRTP } from 'utils/casino';

interface SearchCardProps {
  item: ICasino | ISlot | IProvider;
  handleClose: () => void;
  variant?: string;
}

interface SearchItemProps {
  item: ICasino | ISlot | IProvider;
  handleClose: () => void;
}

interface SearchCardStreamerProps {
  item: IStreamer;
  handleClose: () => void;
}

export const SearchNotFound = () => {
  return (
    <div className={styles.search__no_result}>
      <div className={styles.search__no_result_top}>
        <Icon.ExclamationMark fill="white" />

        <span>{'No results'}</span>
      </div>
      <div className={styles.search__no_result_bottom}>
        <div className={styles.search__no_result_question}>
          {`Can't find your casino or slot?`}
        </div>
        <Link
          prefetch={false}
          href={Routes.contactUs}
          className={styles.search__no_result_contact}
        >
          <span>{'contact us'}</span>
          <Icon.Arrow />
        </Link>
      </div>
    </div>
  );
};

export const SearchCardStreamer = ({
  item,
  handleClose,
}: SearchCardStreamerProps) => {
  const router = useRouter();

  const handleCard = () => {
    router.push(Routes.streamer(item._id));
    handleClose();
  };

  return (
    <div className={styles.search__result} onClick={handleCard}>
      <div className={styles.search__result_card}>
        <div className={styles.search__result_img}>
          <LogoOverlayWrapper>
            <BlurHashImage
              src={item?.imageUrl ?? ''}
              alt={item?.username}
              sizes="70px"
            />
            {'logoUrl' in item && item.logoUrl && (
              <LogoOverlay alt={item?.username} image={item.logoUrl} />
            )}
          </LogoOverlayWrapper>
        </div>
        <div>
          <div className={styles.search__result_name}>{item?.username}</div>
          <div className={styles.search__result_rtp}></div>
        </div>
      </div>
      <div className={styles.search__result_inpsections}>
        <Icon.Search />
      </div>
      <div className={styles.search__result_arrow}>
        <Icon.Arrow />
      </div>
    </div>
  );
};

export const SearchCard = ({ item, handleClose, variant }: SearchCardProps) => {
  const router = useRouter();

  const isCasino = 'rating' in item;
  const isProvider = 'slotsCount' in item;

  const handleCard = () => {
    if (isProvider) {
      router.push(Routes.provider(getItemIdentifier(item)));
    } else if (isCasino) {
      router.push(Routes.casinoPage(getItemIdentifier(item)));
    } else {
      router.push(Routes.slotPage(getItemIdentifier(item)));
    }
    handleClose();
  };

  const { isBad, isGood, isFetching: isRatingFetching } = useRTPWeights();

  const renderIcon = (rtp: number) => {
    if (isRatingFetching) {
      return <Icon.Cherry fill="#FEFEFE" />;
    }
    if (isBad(rtp)) {
      return <Icon.Bad />;
    } else if (isGood(rtp)) {
      return <Icon.Good />;
    } else {
      return <Icon.Cherry />;
    }
  };

  const dynamicStyle = useMemo(() => {
    if ('rating' in item) {
      if (isRatingFetching) {
        return {
          rtpiStatus: 'High RTP',
          classContainer: styles.casino_rating_hight,
        };
      }
      if (isBad(getCasinoRTP(item).number)) {
        return {
          rtpiStatus: 'Very low RTP',
          classContainer: styles.casino_rating_low,
        };
      }
      if (isGood(getCasinoRTP(item).number)) {
        return {
          rtpiStatus: 'Low RTP',
          classContainer: styles.casino_rating_medium,
        };
      }

      return {
        rtpiStatus: 'High RTP. You can win',
        classContainer: styles.casino_rating_hight,
      };
    }
  }, [isBad, isGood, isRatingFetching, item]);

  return (
    <div
      className={clsx(styles.search__result, dynamicStyle, {
        [styles.sidebar_result]: variant === 'sidebar',
      })}
      onClick={() => handleCard()}
    >
      <div className={styles.search__result_card}>
        <div className={styles.search__result_img}>
          <LogoOverlayWrapper>
            <BlurHashImage
              src={item?.imageUrl ?? ''}
              alt={item?.name}
              blurhash={'blurHash' in item ? item.blurHash : undefined}
              sizes="70px"
            />
            {'logoUrl' in item && item.logoUrl && (
              <LogoOverlay alt={item?.name} image={item.logoUrl} />
            )}
          </LogoOverlayWrapper>
        </div>
        <div>
          <div className={styles.search__result_name}>{item?.name}</div>
          <div className={styles.search__result_rtp}>
            {isCasino && (
              <div className={styles.casino_card__rtp}>
                {renderIcon(getCasinoRTP(item).number)}
                <span className={dynamicStyle?.classContainer}>
                  {dynamicStyle?.rtpiStatus}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {variant !== 'sidebar' && (
        <div className={styles.search__result_inpsections}>
          <Icon.Search />
          {!isProvider && (
            <span>
              {(('casinos_reviews_count' in item
                ? item?.casinos_reviews_count
                : 0) ?? 0) +
                ('slots_reviews_count' in item
                  ? (item?.slots_reviews_count ?? 0)
                  : 0)}{' '}
              checks
            </span>
          )}
          {isProvider && (
            <>
              <Icon.Smile />
              <span>{`${item?.slotsCount ?? 0} slots`}</span>
            </>
          )}
        </div>
      )}
      {variant === 'sidebar' && isProvider && (
        <div className={styles.search__result_inpsections}>
          <span
            className={styles.sidebar_slots_count}
          >{`${item?.slotsCount ?? 0} slots`}</span>
        </div>
      )}
      <div className={styles.search__result_arrow}>
        <Icon.Arrow fillOpacity={variant === 'sidebar' ? '1' : '0.4'} />
      </div>
    </div>
  );
};

export const SearchItemBar = ({ item, handleClose }: SearchItemProps) => {
  const router = useRouter();

  const isCasino = 'rating' in item;
  const isProvider = 'slotsCount' in item;

  const handleCard = () => {
    if (isProvider) {
      router.push(Routes.provider(getItemIdentifier(item)));
    } else if (isCasino) {
      router.push(Routes.casinoPage(getItemIdentifier(item)));
    } else {
      router.push(Routes.slotPage(getItemIdentifier(item)));
    }
    handleClose();
  };

  return (
    <div className={styles.search__result} onClick={() => handleCard()}>
      <div className={styles.search__result_card}>
        <div className={styles.search__result_img}>
          <LogoOverlayWrapper>
            <BlurHashImage
              src={item?.imageUrl ?? ''}
              alt={item?.name}
              blurhash={'blurHash' in item ? item.blurHash : undefined}
              sizes="50px"
            />
            {'logoUrl' in item && item.logoUrl && (
              <LogoOverlay alt={item?.name} image={item.logoUrl} />
            )}
          </LogoOverlayWrapper>
        </div>
        <div className={styles.search__result_info}>
          <div
            className={clsx(
              styles.search__result_name,
              styles.search__result_name_bar_width
            )}
          >
            {item?.name}
          </div>
        </div>
      </div>
      <div className={styles.search__result_inpsections}>
        {isProvider && <span>{`${item?.slotsCount ?? 0} slots`}</span>}
      </div>
      <div className={styles.search__result_arrow}>
        <Icon.Arrow />
      </div>
    </div>
  );
};

export const Search = ({
  data,
  handleClose,
  type,
  variant,
  searchPosition = 'absolute',
  wrapperStyle,
  allResults,
}: {
  data: Array<ICasino | ISlot | IProvider>;
  handleClose: () => void;
  type?: 'modal' | 'bar';
  variant?: string;
  searchPosition?: 'absolute' | 'relative';
  wrapperStyle?: React.CSSProperties;
  // Link to the full results page, shown when the dropdown holds only part of the matches
  allResults?: { href: string; count: number };
}) => {
  const searchResults = useMemo(() => {
    if (data && data.length > 0) {
      return data.map((card: ICasino | ISlot | IProvider) => {
        if (variant === 'sidebar') {
          return (
            <SearchCard
              variant={variant}
              item={card}
              handleClose={handleClose}
              key={card._id.toString()}
            />
          );
        }
        if (type === 'modal') {
          if (variant === 'streamer') {
            return (
              <SearchCardStreamer
                item={card as any}
                handleClose={handleClose}
                key={card._id.toString()}
              />
            );
          }
          return (
            <SearchCard
              item={card}
              handleClose={handleClose}
              key={card._id.toString()}
            />
          );
        }
        if (type === 'bar') {
          return (
            <SearchItemBar
              item={card}
              handleClose={handleClose}
              key={card._id.toString()}
            />
          );
        }
        return null;
      });
    } else {
      return <SearchNotFound />;
    }
  }, [data, handleClose, type, variant]);

  return (
    <div
      className={clsx(styles.header__search, styles.search)}
      style={wrapperStyle}
    >
      <div className={styles.search__results}>
        <div className={styles.scroll}>
          <div
            style={{ position: searchPosition ?? 'absolute' }}
            className={styles.search__results_wrapper}
          >
            {searchResults}
            {allResults &&
              data?.length > 0 &&
              allResults.count > data.length && (
                <Link
                  prefetch={false}
                  href={allResults.href}
                  onClick={handleClose}
                  className={styles.search__all_results}
                >
                  <span>{`All results (${allResults.count})`}</span>
                  <Icon.Arrow />
                </Link>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};
